import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Toast } from 'primereact/toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  transactionId: string | null;
  quoteId: string | null;
  status: string | null;
}

const LEGAL_ENTITY: Record<string, string> = {
  '1': 'Sole Proprietor', '2': 'Partnership',
  '3': 'Corporation', '4': 'LLC', '5': 'Other',
};

function fmt(val: string): string {
  if (!val || val === 'null' || val === '0') return '—';
  return val;
}

function fmtMoney(val: string): string {
  if (!val || val === '0' || val === 'null') return '$0';
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  return '$' + n.toLocaleString('en-US');
}

function initials(name: string): string {
  return (name || '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function fmtDate(v?: string): string {
  if (!v || v.trim() === '') return '—';
  return v.replace(/\s+\d{1,2}:\d{2}\s*(am|pm)/i, '').trim() || '—';
}

const CoverageChip: React.FC<{ label: string; active: boolean }> = ({ label, active }) => (
  <span className={`coverage-chip ${active ? 'coverage-chip--yes' : 'coverage-chip--no'}`}>
    {active
      ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
      : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    }
    {label}
  </span>
);

const QuoteDetailDrawer: React.FC<Props> = ({ isOpen, onClose, transactionId, quoteId, status }) => {
  const [detail, setDetail] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  useEffect(() => {
    if (!isOpen || !transactionId) return;
    setIsLoading(true);
    setFetchError(false);
    setDetail(null);

    const token = localStorage.getItem('igtoken');
    axios({
      method: 'get',
      url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/GetQuoteByTransactionId`,
      params: { transactionId },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res: any) => {
        const raw = res.data;
        const parsed = typeof raw?.requestData === 'string'
          ? JSON.parse(raw.requestData)
          : raw;
        setDetail(parsed);
      })
      .catch((error: any) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/login");
        } else {
          setFetchError(true);
          toast.current?.show({
            severity: 'error', summary: 'Error',
            detail: error.response?.data?.message
              ? `Error ${error.response.status}: ${error.response.data.message}`
              : 'Failed to load quote details. Please try again.',
            life: 3000,
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [isOpen, transactionId, navigate]);

  const statusClass = status === 'Approved' ? 'badge--approved'
    : status === 'Rejected' ? 'badge--rejected'
    : 'badge--pending';

  // Safe derived data
  const ins  = detail?.insuredInfo    ?? detail?.InsuredInfo    ?? null;
  const sub  = detail?.submissionInfo ?? detail?.SubmissionInfo ?? null;
  const agcy = detail?.agencyInfo     ?? detail?.AgencyInfo     ?? null;

  const owner    = ins?.owner    ?? { name: '', email: '', phone: '' };
  const vehicles: any[] = (sub?.vehicles ?? []).filter(Boolean);
  const drivers:  any[] = (sub?.drivers  ?? []).filter(Boolean);

  const policyObj = detail?.original_json?.PolicyObject ?? {};

  const drawerPremiumRows: [string, number][] = [
    ...(policyObj.ALPremium  ? [['Auto Liability',    policyObj.ALPremium]  as [string, number]] : []),
    ...(policyObj.GLPremium  ? [['General Liability', policyObj.GLPremium]  as [string, number]] : []),
    ...(policyObj.MedPremium ? [['Medical',           policyObj.MedPremium] as [string, number]] : []),
    ...(policyObj.UMPremium  ? [['UM / UIM',          policyObj.UMPremium]  as [string, number]] : []),
    ...(policyObj.PIPCoverage !== '0' && policyObj.PIPModifications
        ? [['PIP', policyObj.PIPModifications] as [string, number]] : []),
  ];

  const drawerFeeRows: [string, number][] = [
    ...(policyObj.PolicyFee      ? [['Policy Fee',   policyObj.PolicyFee]      as [string, number]] : []),
    ...(policyObj.StateTax       ? [['State Tax',    policyObj.StateTax]       as [string, number]] : []),
    ...(policyObj.DomicileTax    ? [['Domicile Tax', policyObj.DomicileTax]    as [string, number]] : []),
    ...(policyObj.Capitalization ? [['Capital Fee',  policyObj.Capitalization] as [string, number]] : []),
  ];

  const totalPremium = drawerPremiumRows.reduce((s, [, v]) => s + v, 0);
  const totalFees    = drawerFeeRows.reduce((s, [, v]) => s + v, 0);

  return ReactDOM.createPortal(
    <>
      <Toast ref={toast} position="top-right" />
      <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="qd-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            className="qd-panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          >
            {/* ── Header ── */}
            <div className="qd-header">
              <div className="qd-header__left">
                <p className="qd-header__company">{ins?.name || '—'}</p>
                <div className="qd-header__meta">
                  {quoteId && <span className="qd-header__id">{quoteId}</span>}
                </div>
              </div>
              <div className="qd-header__right">
                {status && <span className={`badge ${statusClass}`}>{status}</span>}
                <button className="qd-close-btn" onClick={onClose} aria-label="Close">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* ── Body ── */}
            <div className="qd-body">

              {/* Loading skeleton */}
              {isLoading && (
                <div className="qd-skeleton-wrap">
                  {/* Card 1 — Insured */}
                  <div className="qd-skeleton-card">
                    <div className="qd-skeleton qd-skeleton-title" />
                    <div className="qd-skeleton-row">
                      <div className="qd-skeleton-field">
                        <div className="qd-skeleton qd-skeleton-label" />
                        <div className="qd-skeleton qd-skeleton-value" />
                      </div>
                      <div className="qd-skeleton-field">
                        <div className="qd-skeleton qd-skeleton-label" />
                        <div className="qd-skeleton qd-skeleton-value" />
                      </div>
                    </div>
                    <div className="qd-skeleton-row">
                      <div className="qd-skeleton-field">
                        <div className="qd-skeleton qd-skeleton-label" />
                        <div className="qd-skeleton qd-skeleton-value" />
                      </div>
                      <div className="qd-skeleton-field">
                        <div className="qd-skeleton qd-skeleton-label" />
                        <div className="qd-skeleton qd-skeleton-value" />
                      </div>
                    </div>
                    <div className="qd-skeleton qd-skeleton-line" style={{ width: '70%' }} />
                    <div className="qd-skeleton-row" style={{ alignItems: 'center', gap: 10 }}>
                      <div className="qd-skeleton" style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }} />
                      <div className="qd-skeleton-field">
                        <div className="qd-skeleton qd-skeleton-label" style={{ width: '45%' }} />
                        <div className="qd-skeleton qd-skeleton-value" style={{ width: '65%' }} />
                      </div>
                    </div>
                  </div>

                  {/* Card 2 — Policy & Coverage */}
                  <div className="qd-skeleton-card">
                    <div className="qd-skeleton qd-skeleton-title" />
                    <div className="qd-skeleton-row">
                      <div className="qd-skeleton-field">
                        <div className="qd-skeleton qd-skeleton-label" />
                        <div className="qd-skeleton qd-skeleton-value" />
                      </div>
                      <div className="qd-skeleton" style={{ width: 24, height: 24, borderRadius: '50%', alignSelf: 'center', flexShrink: 0 }} />
                      <div className="qd-skeleton-field">
                        <div className="qd-skeleton qd-skeleton-label" />
                        <div className="qd-skeleton qd-skeleton-value" />
                      </div>
                    </div>
                    <div className="qd-skeleton-chips">
                      {[72, 48, 60, 80, 56, 64, 90, 52].map((w, i) => (
                        <div key={i} className="qd-skeleton qd-skeleton-chip" style={{ width: w }} />
                      ))}
                    </div>
                  </div>

                  {/* Card 3 — Premiums */}
                  <div className="qd-skeleton-card">
                    <div className="qd-skeleton qd-skeleton-title" />
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="qd-skeleton-row" style={{ justifyContent: 'space-between' }}>
                        <div className="qd-skeleton qd-skeleton-line" style={{ width: '48%' }} />
                        <div className="qd-skeleton qd-skeleton-line" style={{ width: '22%' }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error */}
              {!isLoading && fetchError && (
                <div style={{ padding: '24px', color: '#dc2626', textAlign: 'center' }}>
                  Failed to load quote details.
                </div>
              )}

              {/* Content */}
              {!isLoading && !fetchError && ins && sub && (
                <>
                  {/* ── Insured Information ── */}
                  <div className="section-card">
                    <div className="section-card__title">
                      <span className="section-card__title-bar" />
                      <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                      Insured Information
                    </div>
                    <div className="section-card__body">
                      <div className="qd-grid">
                        <div className="detail-field">
                          <span className="detail-field__label">Legal Entity</span>
                          <span className="detail-field__value">{LEGAL_ENTITY[ins.legalEntity] || ins.legalEntity}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-field__label">Year Established</span>
                          <span className="detail-field__value">{fmt(ins.yearEstablished)}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-field__label">DOT Number</span>
                          <span className="detail-field__value">{fmt(ins.dotNumber)}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-field__label">MC Number</span>
                          <span className={`detail-field__value ${ins.mcNumber === 'null' ? 'detail-field__value--muted' : ''}`}>
                            {ins.mcNumber === 'null' ? 'N/A' : ins.mcNumber}
                          </span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-field__label">Experience</span>
                          <span className="detail-field__value">{ins.experienceYears} yr{+ins.experienceYears !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-field__label">Insured ID</span>
                          <span className={`detail-field__value ${ins.insuredId === 'null' ? 'detail-field__value--muted' : ''}`}>
                            {ins.insuredId === 'null' ? 'Not assigned' : ins.insuredId}
                          </span>
                        </div>
                      </div>

                      <div className="qd-divider" />

                      <div className="qd-address">
                        <svg className="qd-address__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                        <span className="qd-address__text">
                          {ins.address}, {ins.city}, {ins.state} {ins.zipCode}
                        </span>
                      </div>

                      <div className="qd-divider" />

                      <div className="qd-contact">
                        <div className="qd-contact__avatar">{initials(owner.name)}</div>
                        <div className="qd-contact__info">
                          <p className="qd-contact__name">{owner.name}</p>
                          <div className="qd-contact__details">
                            <span className="qd-contact__detail">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                              {owner.email}
                            </span>
                            <span className="qd-contact__detail">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.26h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.72 17.5z"/></svg>
                              {owner.phone}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Policy Period & Coverage ── */}
                  <div className="section-card">
                    <div className="section-card__title">
                      <span className="section-card__title-bar" />
                      <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      Policy Period &amp; Coverage
                    </div>
                    <div className="section-card__body">
                      <div className="qd-period">
                        <div className="qd-period__date">
                          <span className="qd-period__label">Effective</span>
                          <span className="qd-period__value">{fmtDate(sub.effectiveDate)}</span>
                        </div>
                        <svg className="qd-period__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                        </svg>
                        <div className="qd-period__date">
                          <span className="qd-period__label">Expiration</span>
                          <span className="qd-period__value">{fmtDate(sub.expirationDate)}</span>
                        </div>
                      </div>

                      <div className="qd-divider" />

                      <div className="qd-coverages">
                        <CoverageChip label="General Liability"  active={sub.generalLiability === 'yes'} />
                        <CoverageChip label="PIP"                active={sub.pipCoverage === 'yes'} />
                        <CoverageChip label="UIIA"               active={sub.uiia === 'yes'} />
                        <CoverageChip label="UM / UIM"           active={sub.unUnderinsuredMotorist === 'yes'} />
                        <CoverageChip label="Medical"            active={sub.medicalCoverage === 'yes'} />
                        <CoverageChip label="Auto Med Pay"       active={sub.autoMedicalPayments === 'yes'} />
                        <CoverageChip label="Reefer Breakdown"   active={sub.reeferBreakdown?.toLowerCase() === 'yes'} />
                        <CoverageChip label="Cargo"              active={sub.needCargo?.toLowerCase() === 'yes'} />
                        <CoverageChip label="Terminal"           active={sub.terminal === 'yes'} />
                      </div>

                      {sub.uiia === 'yes' && sub.uiiEndorsement && (
                        <>
                          <div className="qd-divider" />
                          <div className="detail-field">
                            <span className="detail-field__label">UIIA Endorsement</span>
                            <span className="detail-field__value">{sub.uiiEndorsement}</span>
                          </div>
                        </>
                      )}

                      {sub.generalLiability === 'yes' && (
                        <>
                          <div className="qd-divider" />
                          <div className="detail-field">
                            <span className="detail-field__label">Liability Limit</span>
                            <span className="detail-field__value">{sub.liabilityLimit}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* ── Premiums & Fees ── */}
                  <div className="section-card">
                    <div className="section-card__title">
                      <span className="section-card__title-bar" />
                      <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                      Premiums &amp; Fees
                    </div>
                    <div className="section-card__body">
                      <table className="qd-table">
                        <tbody>
                          {drawerPremiumRows.map(([label, value]) => (
                            <tr key={label}>
                              <td>{label}</td>
                              <td>{fmtMoney(String(value))}</td>
                            </tr>
                          ))}
                          <tr className="qd-table__subtotal">
                            <td>Premium Subtotal</td>
                            <td>{fmtMoney(String(totalPremium))}</td>
                          </tr>
                          {drawerFeeRows.map(([label, value]) => (
                            <tr key={label}>
                              <td>{label}</td>
                              <td>{fmtMoney(String(value))}</td>
                            </tr>
                          ))}
                          <tr className="qd-table__total">
                            <td>Total</td>
                            <td>{fmtMoney(String(totalPremium + totalFees))}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ── Vehicles ── */}
                  {vehicles.length > 0 && (
                    <div className="section-card">
                      <div className="section-card__title">
                        <span className="section-card__title-bar" />
                        <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                        </svg>
                        Vehicles ({vehicles.length})
                      </div>
                      <div className="section-card__body">
                        {vehicles.map((v: any, i: number) => (
                          <div className="qd-item-card" key={i}>
                            <div className="qd-item-card__icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                              </svg>
                            </div>
                            <div className="qd-item-card__content">
                              <p className="qd-item-card__title">{v.year} {v.make} — {v.type}</p>
                              <p className="qd-item-card__vin">VIN: {v.vin}</p>
                              <div className="qd-item-card__tags">
                                <span className="qd-tag">Value: {fmtMoney(v.value)}</span>
                                <span className="qd-tag">Deductible: {fmtMoney(v.deductible)}</span>
                                <span className="qd-tag">Radius: {v.operatingRadius}</span>
                                <span className="qd-tag">Team Driver: {v.teamDriver}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Drivers ── */}
                  {drivers.length > 0 && (
                    <div className="section-card">
                      <div className="section-card__title">
                        <span className="section-card__title-bar" />
                        <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                        </svg>
                        Drivers ({drivers.length})
                      </div>
                      <div className="section-card__body">
                        {drivers.map((d: any, i: number) => (
                          <div className="qd-item-card" key={i}>
                            <div className="qd-item-card__icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                              </svg>
                            </div>
                            <div className="qd-item-card__content">
                              <p className="qd-item-card__title">{d.name}, Age {d.age}</p>
                              <p className="qd-item-card__vin">
                                License: {d.licenseNumber} (Class {d.licenseClass}) · {d.state}
                              </p>
                              <div className="qd-item-card__tags">
                                <span className="qd-tag">DOB: {fmtDate(d.dateOfBirth)}</span>
                                <span className="qd-tag">Violations: {d.violations}</span>
                                <span className="qd-tag">Owner: {d.owner}</span>
                                <span className="qd-tag">Valid License: {d.validLicense}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Agency ── */}
                  {agcy && (
                    <div className="section-card">
                      <div className="section-card__title">
                        <span className="section-card__title-bar" />
                        <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                        </svg>
                        Agency
                      </div>
                      <div className="section-card__body">
                        <div className="qd-agency">
                          <div className="qd-agency__logo">{initials(agcy.name)}</div>
                          <div className="qd-agency__info">
                            <p className="qd-agency__name">{agcy.name}</p>
                            <p className="qd-agency__id">{agcy.agencyId}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </>,
    document.body
  );
};

export default QuoteDetailDrawer;
